import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "@common/store/hooks";
import { Icon, IconButton } from "@jkast90/kast-common/ui";
import cta from "./cta.module.css";
import styles from "./FloatingCartButton.module.css";

// Routes where the floating cart should never appear: the cart page itself
// (you're already there) and the rest of the checkout flow (don't tempt the
// user to bounce back mid-purchase).
const HIDDEN_PATTERNS = [/^\/shop\/cart\b/, /^\/shop\/checkout\b/];

// Only show on /shop and its sub-routes — otherwise the button intrudes on
// non-shop pages where it makes no sense.
const SHOP_ROUTE = /^\/shop\b/;

export default function FloatingCartButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const lines = useAppSelector((s) => s.cart.lines);
  const itemCount = lines.reduce((n, l) => n + l.quantity, 0);

  // Briefly bump-animate the button each time the count goes up, so users
  // notice their add-to-cart action registered even though the cart is in
  // the corner.
  const prevCountRef = useRef(itemCount);
  const [bump, setBump] = useState(false);
  useEffect(() => {
    if (itemCount > prevCountRef.current) {
      setBump(true);
      const t = window.setTimeout(() => setBump(false), 400);
      prevCountRef.current = itemCount;
      return () => window.clearTimeout(t);
    }
    prevCountRef.current = itemCount;
  }, [itemCount]);

  const onShopRoute = SHOP_ROUTE.test(location.pathname);
  const onHiddenRoute = HIDDEN_PATTERNS.some((re) => re.test(location.pathname));
  if (!onShopRoute || onHiddenRoute || itemCount === 0) {
    return null;
  }

  return (
    <IconButton
      label="Cart"
      variant="primary"
      badge={itemCount}
      className={`${cta.cta} ${styles.button} ${bump ? styles.bump : ""}`}
      onClick={() => navigate("/shop/cart")}
    >
      <Icon name="cart" size={22} />
    </IconButton>
  );
}
